import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ReceivedData {
  totalReceived: number;
  receivedBySource: Map<string, number>;
  receivedByConsultant: Map<string, { total: number; bySource: Map<string, number> }>;
}

/**
 * Fetch cash received (payments with status='paid' and paid_at in period)
 * Joins with deals to get deals_source and sales_consultant
 */
export function useReceivedByPeriod(
  dateRange: { from: Date | null; to: Date | null } | null,
  businessUnit: string = 'all'
) {
  return useQuery({
    queryKey: [
      'received-by-period',
      dateRange?.from?.toISOString(),
      dateRange?.to?.toISOString(),
      businessUnit,
    ],
    queryFn: async (): Promise<ReceivedData> => {
      const fromDate = dateRange?.from?.toISOString();
      const toDate = dateRange?.to?.toISOString();

      if (!fromDate || !toDate) {
        return { totalReceived: 0, receivedBySource: new Map(), receivedByConsultant: new Map() };
      }

      // Map BU filter
      const buFilter = businessUnit !== 'all' ? businessUnit : null;

      // Use YYYY-MM-DD for DATE column (deals.closed_date) to avoid timezone mismatch
      const closedFrom = fromDate.split('T')[0];
      const closedTo = toDate.split('T')[0];

      let query = supabase
        .from('payments')
        .select('paid_amount, deal_id, deals!inner(deals_source, sales_consultant, business_unit, closed_date, stage)')
        .eq('status', 'paid')
        .gte('paid_at', fromDate)
        .lte('paid_at', toDate)
        .eq('deals.stage', 'won')
        .gte('deals.closed_date', closedFrom)
        .lte('deals.closed_date', closedTo);

      if (buFilter) {
        query = query.eq('deals.business_unit', buFilter);
      }

      const { data, error } = await query;
      if (error) {
        console.error('[useReceivedByPeriod] error:', error);
        // Return empty data instead of crashing the dashboard
        return { totalReceived: 0, receivedBySource: new Map(), receivedByConsultant: new Map() };
      }

      let totalReceived = 0;
      const bySource = new Map<string, number>();
      const byConsultant = new Map<string, { total: number; bySource: Map<string, number> }>();

      (data ?? []).forEach((row: any) => {
        const amount = Number(row.paid_amount) || 0;
        const deal = row.deals;
        const source = deal?.deals_source || 'Other';
        const consultantId = deal?.sales_consultant;

        totalReceived += amount;

        // By source
        bySource.set(source, (bySource.get(source) || 0) + amount);

        // By consultant
        if (consultantId) {
          if (!byConsultant.has(consultantId)) {
            byConsultant.set(consultantId, { total: 0, bySource: new Map() });
          }
          const entry = byConsultant.get(consultantId)!;
          entry.total += amount;
          entry.bySource.set(source, (entry.bySource.get(source) || 0) + amount);
        }
      });

      return { totalReceived, receivedBySource: bySource, receivedByConsultant: byConsultant };
    },
    enabled: !!dateRange?.from && !!dateRange?.to,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}
